/**
 * Authenticated app shell — sidebar + main content area.
 * Full 8-screen client portal wired up.
 */

import React, { useState } from 'react';
import { Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ContactRateLogo } from '../components/ContactRateLogo.js';
import { useIsMobile } from '../lib/hooks.js';
import type { ApiUser } from '@contactrate/shared';

// ── Screens ─────────────────────────────────────────────────────────────────
import { DashboardPage } from './screens/DashboardPage.js';
import { LivePage } from './screens/LivePage.js';
import { InboxPage } from './screens/InboxPage.js';
import { PipelinePage } from './screens/PipelinePage.js';
import { LeadsPage } from './screens/LeadsPage.js';
import { CalendarPage } from './screens/CalendarPage.js';
import { AutomationsPage } from './screens/AutomationsPage.js';
import { SettingsPage } from './screens/SettingsPage.js';
import { DemoTopBar, CrmUpsellCard } from '../components/ManagedChrome.js';
import { usePortal } from '../portal/PortalContext.js';

// ── Icons ────────────────────────────────────────────────────────────────────

const Icons = {
  Dashboard: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
      <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
    </svg>
  ),
  Live: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/><path d="M6.3 6.3a8 8 0 0 0 0 11.4"/><path d="M17.7 6.3a8 8 0 0 1 0 11.4"/>
    </svg>
  ),
  Inbox: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  ),
  Pipeline: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="5" height="16" rx="1"/><rect x="9.5" y="4" width="5" height="16" rx="1"/><rect x="17" y="4" width="5" height="16" rx="1"/>
    </svg>
  ),
  Leads: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  Calendar: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  ),
  Automations: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
    </svg>
  ),
  Settings: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
    </svg>
  ),
  ChevronLeft: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6"/>
    </svg>
  ),
  LogOut: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  ),
};

// ── AI Status card ────────────────────────────────────────────────────────────

function AIStatusCard({ aiName, collapsed }: { aiName: string; collapsed: boolean }) {
  if (collapsed) {
    return (
      <div className="flex justify-center mb-2">
        <div className="relative w-8 h-8 rounded-full flex items-center justify-center cursor-pointer"
          style={{ background: 'linear-gradient(135deg, #8F6BFF 0%, #4A63E8 100%)' }}>
          <span className="text-white text-[11px]">✦</span>
          <div className="absolute rounded-full border-2 pulse-ring" style={{ inset: -3, borderColor: '#159662' }}/>
        </div>
      </div>
    );
  }
  return (
    <div className="mx-2 mb-3 px-3 py-2.5 rounded-[10px] cursor-pointer"
      style={{ background: 'rgba(109,74,234,0.06)', border: '1px solid rgba(109,74,234,0.18)' }}>
      <div className="flex items-center gap-2.5">
        <div className="relative flex-shrink-0">
          <div className="w-[26px] h-[26px] rounded-full flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #8F6BFF 0%, #4A63E8 100%)' }}>
            <span className="text-white text-[10px]">✦</span>
          </div>
          <div className="absolute rounded-full border-2 pulse-ring"
            style={{ inset: -3, borderColor: '#159662' }}/>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[12px] font-semibold text-ink truncate">{aiName}</p>
          <p className="text-[9.5px] font-medium truncate"
            style={{ fontFamily: '"DM Mono", monospace', color: '#159662' }}>
            Working your leads · managed
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Nav item definition ───────────────────────────────────────────────────────

interface NavItem {
  path: string;
  label: string;
  Icon: () => React.ReactElement;
  badge?: string;
}

const CLIENT_NAV: NavItem[] = [
  { path: '/dashboard', label: 'Dashboard', Icon: Icons.Dashboard },
  { path: '/live', label: 'Live', Icon: Icons.Live, badge: '1' },
  { path: '/inbox', label: 'Inbox', Icon: Icons.Inbox, badge: '2' },
  { path: '/pipeline', label: 'Pipeline', Icon: Icons.Pipeline },
  { path: '/leads', label: 'Leads', Icon: Icons.Leads },
  { path: '/calendar', label: 'Calendar', Icon: Icons.Calendar },
  { path: '/automations', label: 'Automations', Icon: Icons.Automations },
  { path: '/settings', label: 'Settings', Icon: Icons.Settings },
];

// ── Sidebar ──────────────────────────────────────────────────────────────────

function Sidebar({ user, aiName, collapsed, onCollapse, onLogout }: {
  user: ApiUser;
  aiName: string;
  collapsed: boolean;
  onCollapse: () => void;
  onLogout: () => void;
}) {
  const w = collapsed ? 64 : 224;

  return (
    <aside className="fixed left-0 flex flex-col z-40 transition-all duration-200"
      style={{
        width: w,
        top: 52,
        height: 'calc(100dvh - 52px)',
        background: '#FFFFFF',
        borderRight: '1px solid #E5EAF2',
      }}>

      {/* Logo */}
      <div className="flex items-center px-4 py-4 flex-shrink-0"
        style={{ minHeight: 60, borderBottom: '1px solid #F0F3F8' }}>
        {collapsed ? (
          <div className="w-full flex justify-center">
            <svg width="22" height="22" viewBox="0 0 40 40" fill="none">
              <rect x="0" y="24" width="6" height="10" rx="2" fill="#2E66E0" opacity="0.4"/>
              <rect x="9" y="17" width="6" height="17" rx="2" fill="#2E66E0" opacity="0.65"/>
              <rect x="18" y="10" width="6" height="24" rx="2" fill="#2E66E0"/>
              <rect x="27" y="4" width="6" height="30" rx="2" fill="#2E66E0"/>
            </svg>
          </div>
        ) : (
          <ContactRateLogo height={22}/>
        )}
      </div>

      {/* Workspace label */}
      {!collapsed && (
        <div className="px-4 pt-3 pb-1 flex-shrink-0">
          <span className="block text-[9px] font-semibold uppercase tracking-[0.1em]"
            style={{ color: 'rgba(13,23,41,0.25)' }}>
            Managed client portal
          </span>
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 px-2 py-1 overflow-y-auto">
        {CLIENT_NAV.map(({ path, label, Icon: NavIcon, badge }) => (
          <NavLink key={path} to={path}
            className={({ isActive }) =>
              ['flex items-center gap-[11px] rounded-[10px] px-[11px] py-[8px] mb-0.5 transition-all text-[13.5px] font-medium',
               isActive ? 'nav-active' : 'hover:bg-surface-alt'].join(' ')
            }
            style={({ isActive }) => ({ color: isActive ? '#D97706' : 'rgba(13,23,41,0.5)' })}
            title={collapsed ? label : undefined}>
            {({ isActive }) => (
              <>
                <span className="flex-shrink-0" style={{ color: isActive ? '#D97706' : undefined }}>
                  <NavIcon/>
                </span>
                {!collapsed && (
                  <>
                    <span className="flex-1 truncate">{label}</span>
                    {badge && (
                      <span className="text-[10px] font-medium text-white rounded-[999px] px-1.5 py-0.5"
                        style={{ fontFamily: '"DM Mono", monospace', background: '#D97706', minWidth: 18, textAlign: 'center' }}>
                        {badge}
                      </span>
                    )}
                  </>
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className="flex-shrink-0 px-2 pb-3 pt-2" style={{ borderTop: '1px solid #F0F3F8' }}>
        <AIStatusCard aiName={aiName} collapsed={collapsed}/>
        <div className="flex items-center gap-2 px-2 py-1.5">
          <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-[11px] font-semibold text-white"
            style={{ background: '#2E66E0' }}>
            {user.email[0]?.toUpperCase()}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-semibold text-ink truncate">{user.email}</p>
              <p className="text-[10px] text-muted capitalize">{user.role}</p>
            </div>
          )}
          {!collapsed && (
            <button onClick={onLogout}
              className="flex-shrink-0 p-1 rounded-[6px] text-muted hover:text-red-DEFAULT hover:bg-red-DEFAULT/10 transition-colors"
              title="Sign out">
              <Icons.LogOut/>
            </button>
          )}
        </div>
        <button onClick={onCollapse}
          className="mt-1 w-full flex items-center justify-center p-1.5 rounded-[8px] text-muted hover:bg-surface-alt transition-colors"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
          <span style={{ display: 'inline-block', transform: collapsed ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
            <Icons.ChevronLeft/>
          </span>
        </button>
      </div>
    </aside>
  );
}

// ── Bottom nav (mobile) ───────────────────────────────────────────────────────

const BOTTOM_NAV: NavItem[] = [
  { path: '/dashboard', label: 'Dashboard', Icon: Icons.Dashboard },
  { path: '/live',      label: 'Live',      Icon: Icons.Live,      badge: '3' },
  { path: '/inbox',     label: 'Inbox',     Icon: Icons.Inbox,     badge: '3' },
  { path: '/pipeline',  label: 'Pipeline',  Icon: Icons.Pipeline },
  { path: '/leads',     label: 'Leads',     Icon: Icons.Leads },
  { path: '/settings',  label: 'Settings',  Icon: Icons.Settings },
];

function BottomNav() {
  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 flex items-stretch"
      style={{
        height: 60,
        background: '#FFFFFF',
        borderTop: '1px solid #E5EAF2',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {BOTTOM_NAV.map(({ path, label, Icon: NavIcon, badge }) => (
        <NavLink
          key={path}
          to={path}
          className="flex-1 flex flex-col items-center justify-center gap-0.5 relative"
          style={({ isActive }) => ({
            color: isActive ? '#D97706' : 'rgba(13,23,41,0.45)',
            minHeight: 44,
          })}
        >
          {({ isActive }) => (
            <>
              <span style={{ color: isActive ? '#D97706' : undefined }} className="relative">
                <NavIcon />
                {badge && (
                  <span
                    className="absolute -top-1 -right-2 w-3.5 h-3.5 rounded-full flex items-center justify-center text-[8px] font-semibold text-white"
                    style={{ background: '#D97706', fontFamily: '"DM Mono", monospace' }}
                  >
                    {badge}
                  </span>
                )}
              </span>
              <span
                className="text-[9px] font-medium"
                style={{ fontFamily: '"DM Sans", system-ui' }}
              >
                {label}
              </span>
              {isActive && (
                <span
                  className="absolute top-0 left-1/2 -translate-x-1/2 w-6 h-0.5 rounded-full"
                  style={{ background: '#D97706' }}
                />
              )}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

// ── App shell ─────────────────────────────────────────────────────────────────

interface Props {
  user: ApiUser;
  onLogout: () => void;
}

export function AppShell({ user, onLogout }: Props) {
  const [collapsed, setCollapsed] = useState(false);
  const [aiName, setAiName] = useState('Ava');
  const isMobile = useIsMobile();
  const sidebarW = collapsed ? 64 : 224;
  const { caps } = usePortal();

  // Sync AI name from settings on mount
  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const { api } = await import('../lib/api.js');
      const { data } = await api.get<{ aiName?: string }>('/api/settings');
      return data;
    },
    enabled: user.role === 'client',
  });

  React.useEffect(() => {
    if (settings?.aiName) setAiName(settings.aiName);
  }, [settings?.aiName]);

  // Padding at bottom on mobile so content clears the bottom nav bar
  const bottomPad = isMobile ? 60 : 0;

  return (
    <div className="flex flex-col" style={{ minHeight: '100dvh', overflowX: 'hidden' }}>
      <DemoTopBar />

      <div className="flex flex-1 min-h-0">
        {/* Sidebar — hidden on mobile */}
        {!isMobile && (
          <Sidebar user={user} aiName={aiName} collapsed={collapsed}
            onCollapse={() => setCollapsed((c) => !c)} onLogout={onLogout}/>
        )}

        <main
          className="flex-1 min-w-0 transition-all duration-200"
          style={{
            marginLeft: isMobile ? 0 : sidebarW,
            background: '#FAFBFC',
            minHeight: 0,
          }}
        >
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace/>}/>

            {/* Screens with standard padding */}
            {[
              { path: '/dashboard', el: <DashboardPage aiName={aiName}/> },
              { path: '/live', el: <LivePage aiName={aiName}/> },
              { path: '/pipeline', el: <PipelinePage aiName={aiName}/> },
              { path: '/leads', el: <LeadsPage aiName={aiName}/> },
              { path: '/calendar', el: <CalendarPage/> },
              { path: '/automations', el: <AutomationsPage/> },
              { path: '/settings', el: <SettingsPage aiName={aiName} onAiNameChange={setAiName}/> },
            ].map(({ path, el }) => (
              <Route key={path} path={path} element={
                <div
                  className="px-4 py-5 md:px-7 md:py-6 mx-auto"
                  style={{ maxWidth: 1240 + 56, paddingBottom: Math.max(24, bottomPad + 16) }}
                >
                  {path === '/dashboard' && <CrmUpsellCard />}
                  {(path === '/pipeline' || path === '/leads') && (
                    <div className="mb-4">
                      {caps.canDispositionLeads ? (
                        <p className="text-[12px] font-medium" style={{ color: '#159662' }}>
                          Workspace CRM on — you can disposition leads. Ava still owns dialing & compliance.
                        </p>
                      ) : (
                        <p className="text-[12px] text-muted">
                          <span className="font-semibold text-ink">View-only theater</span>
                          {' '}— pipeline & leads show Ava’s work. Unlock CRM to edit stages.
                        </p>
                      )}
                    </div>
                  )}
                  {el}
                </div>
              }/>
            ))}

            {/* Inbox — edge-to-edge */}
            <Route path="/inbox" element={
              <div style={{
                height: `calc(100dvh - ${bottomPad + 48}px)`,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}>
                <InboxPage aiName={aiName}/>
              </div>
            }/>

            <Route path="*" element={<Navigate to="/dashboard" replace/>}/>
          </Routes>
        </main>

        {/* Mobile bottom navigation */}
        {isMobile && <BottomNav/>}
      </div>
    </div>
  );
}
